import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import { authenticationState, customerState } from '@repo/unicity'
import { checkoutUuidAtom } from '@/core/checkout/checkout.store'
import {
    omanFormatDataAtom,
    omanSelectedShippingMethodAtom,
    isLoadingOmanFormatAtom,
    omanSavedCardsAtom,
    omanDeliveryFreightAtom,
    omanProcessingPaymentAtom,
    omanProcessingPaymentModalOpenAtom,
    omanCheckoutErrorsAtom,
    omanDeliveryFreightSyncedUuidAtom,
    initialOmanProcessingPayment
} from '../../stores/oman.store'
import { getOmanFormat, fetchOmanSavedCards } from '../../services/oman.api'
import { useOmanGeo } from '../../hooks/useOmanGeo'
import { getHydraFreightAmount, mapOmanFormatData } from '../../utils/oman-format.mapper'
import { isOmanUuidExpiredError, parseOmanAxiosError } from '../../utils/oman-error.helper'
import { resolveOmanShippingMethod } from '../../utils/oman-shipping-methods'

export const useOmanPayments = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const setUuid = useSetAtom(checkoutUuidAtom)
    const setFormatData = useSetAtom(omanFormatDataAtom)
    const setSavedCards = useSetAtom(omanSavedCardsAtom)
    const setLoadingFormat = useSetAtom(isLoadingOmanFormatAtom)
    const setSelectedShippingMethod = useSetAtom(omanSelectedShippingMethodAtom)
    const setDeliveryFreight = useSetAtom(omanDeliveryFreightAtom)
    const setProcessingPayment = useSetAtom(omanProcessingPaymentAtom)
    const setProcessingPaymentModalOpen = useSetAtom(omanProcessingPaymentModalOpenAtom)
    const setCheckoutErrors = useSetAtom(omanCheckoutErrorsAtom)
    const setDeliveryFreightSyncedUuid = useSetAtom(omanDeliveryFreightSyncedUuidAtom)

    const loadingFormat = useAtomValue(isLoadingOmanFormatAtom)
    const formatData = useAtomValue(omanFormatDataAtom)
    const authentication = useAtomValue(authenticationState)
    const customer = useAtomValue(customerState)

    const [loadedForUuid, setLoadedForUuid] = useState<string | null>(null)
    const [loadErrors, setLoadErrors] = useState<string[]>([])

    useOmanGeo()

    useEffect(() => {
        const uuid = searchParams.get('uuid')
        if (!uuid) {
            navigate('/checkout/shipping')
            return
        }

        setUuid(uuid)
        setProcessingPayment(initialOmanProcessingPayment)
        setProcessingPaymentModalOpen(false)
        setCheckoutErrors([])
        setDeliveryFreightSyncedUuid(null)

        setLoadingFormat(true)
        setLoadedForUuid(null)
        setLoadErrors([])
        ;(async () => {
            try {
                const rawFormat = await getOmanFormat(uuid)
                setFormatData({ ...rawFormat, uuid })
                const mapped = mapOmanFormatData(rawFormat, uuid)
                const resolvedMethod = resolveOmanShippingMethod(mapped.shipment_options)
                const freightAmount = getHydraFreightAmount(rawFormat)
                setSelectedShippingMethod(resolvedMethod)
                if (resolvedMethod === 'delivery' || freightAmount > 0) {
                    setDeliveryFreight(freightAmount)
                }
                setLoadedForUuid(uuid)
            } catch (e) {
                if (isOmanUuidExpiredError(e)) {
                    navigate('/', { replace: true })
                    return
                }
                setLoadErrors(parseOmanAxiosError(e, 'failed_to_load_payment_data'))
            } finally {
                setLoadingFormat(false)
            }
        })()
    }, [
        searchParams,
        setUuid,
        setFormatData,
        setLoadingFormat,
        setSelectedShippingMethod,
        setDeliveryFreight,
        setProcessingPayment,
        setProcessingPaymentModalOpen,
        setCheckoutErrors,
        setDeliveryFreightSyncedUuid,
        navigate
    ])

    const customerCountry = customer?.mainAddress?.country
    useEffect(() => {
        if (!authentication.token || !authentication.baId || !customerCountry) return
        fetchOmanSavedCards(authentication.baId, authentication.token, customerCountry)
            .then(res => setSavedCards(res.items ?? []))
            .catch(() => {
                /* ignore */
            })
    }, [authentication.token, authentication.baId, customerCountry, setSavedCards])

    const currentUuid = searchParams.get('uuid')
    const hasMatchingData = !!formatData && formatData.uuid === currentUuid
    const hasLoadedForCurrent = loadedForUuid === currentUuid
    const isLoading = !formatData || !hasMatchingData || !hasLoadedForCurrent || loadingFormat

    return {
        isLoading,
        hasLoadError: loadErrors.length > 0 && !hasLoadedForCurrent,
        loadErrors
    }
}
